/**
 * The board's menus and selecting several (ADR 0194, decisions 3 and 5), for
 * the day view: what a card's right-click menu and the selection bar do,
 * through the existing API, and where focus goes after.
 *
 * - **A card's menu** opens on a right-click on a card (a block's or a
 *   ticket's, never a dashed one) or a ribbon capsule, and with ☰ or
 *   Shift+F10 on a focused card. The browser's own menu stays everywhere
 *   else, on a phone, with touch, and while a popup is open. With several
 *   selected, a right-click on one of them has the selection's menu; on
 *   another card, the selection clears and that card's own menu opens.
 * - **One card's actions:** Dismiss ▸'s reasons dismiss at once
 *   (`POST /blocks/{id}/dismiss`), Merge with ▸ merges at once (`POST
 *   /blocks/{id}/merge`, ADR 0118), the "…" items open the popup on their
 *   pane (`openBlock`), and Copy copies, or shows the text to copy by hand.
 *   A card that leaves the board takes focus on to the next card.
 * - **The selection's:** Dismiss (one reason for all), Merge into one (each
 *   into the earliest, a pair at a time; stopping at a failure and saying
 *   which merged), Mark as vendor… (`POST /blocks/{id}/vendor`, one by one)
 *   and Close tickets… (`POST /tickets/close`, as the tray's Close selected).
 *   What couldn't be done stays selected, the bar saying why; otherwise the
 *   selection clears and focus goes on to the next card left.
 * - **The keys** (`boardKeys`): Ctrl+Space, Shift+↑ ↓ and Esc.
 *
 * Desktop only: the selection clears at a phone's width.
 */
import { useQueryClient } from "@tanstack/react-query";
import {
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError } from "../../api/client";
import type { DismissReason, Submission, TrayTicket } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Menu } from "../../components/ui/Menu";
import { type MenuControl, useMenu } from "../../components/ui/useMenu";
import { formatPhone } from "../../lib/phones";
import { usePrivacy } from "../../lib/privacy";
import type { CardBoard } from "./cardBoard";
import type { OpenOptions } from "./BlockDetails";
import type { BoardCard } from "./board";
import { registerSelection } from "./boardKeys";
import {
  type BulkMenuActions,
  type CardMenuActions,
  bulkMenuItems,
  cardMenuHeader,
  cardMenuItems,
  cardMenuLabel,
} from "./cardMenu";
import { type BlockView, DISMISS_REASONS, vendorParties } from "./model";
import { type BarConfirm, BoardNotice, type Notice, SelectionBar } from "./SelectionBar";
import {
  NO_SELECTION,
  type PickableCard,
  type Selection,
  andList,
  anchorAt,
  blocksToLog,
  bulkActions,
  columnKeys,
  count,
  extend,
  mergeOrder,
  nearCard,
  only,
  pickableCards,
  pickedCards,
  prune,
  rangeTo,
  selectionSummary,
  ticketsToClose,
  timesOf,
  toggle,
} from "./selection";
import { ticketActionQuery } from "./ticketActions";
import { ticketName } from "./tickets";
import { usePhone } from "./usePeek";

/** How long "Copied: …" stays at the foot of the board. */
export const COPIED_MS = 4000;
/** How long "Dismissed …" and its Undo stay there (Discussion #877). */
export const DISMISSED_MS = 8000;
/** A contextmenu this soon after a touch is the touch's: the browser's own. */
const TOUCH_MS = 1000;
/** The ☰ key's own contextmenu, this soon after its keydown opened the menu, is that menu's. */
const MENU_KEY_MS = 500;

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "you can only change your own blocks";
    }
    return error.fieldErrors[0]?.message ?? error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "something went wrong";
}

/** The browser's clipboard, where it has one. */
function clipboard(): Clipboard | undefined {
  return (navigator as { clipboard?: Clipboard }).clipboard;
}

function keyOfBlock(id: string): string {
  return `block:${id}`;
}

export interface BoardActions {
  /** A card's part in the board's menu and selection; none for a dashed card. */
  boardFor: (card: BoardCard) => CardBoard | undefined;
  /** A right-click on a ribbon capsule: its card's menu. */
  onCapsuleMenu: (blockId: string, event: MouseEvent<HTMLElement>) => void;
  /** A click on the board's empty space: the selection clears. */
  onBoardClick: (event: MouseEvent<HTMLElement>) => void;
  /** The selection bar is showing: the page keeps room for it under the board. */
  barShown: boolean;
  /** The card menu, the selection bar and the notice above it, for the day view to render. */
  overlays: ReactNode;
  /** For tests and the day view: the card menu's control. */
  menu: MenuControl;
  /** Blocks just dismissed elsewhere (the popup): said, with Undo for a while (Discussion #877). */
  dismissed: (blocks: readonly BlockView[], reason: DismissReason) => void;
}

export function useBoardActions({
  cards,
  blocks,
  targets,
  timeZone,
  popupOpen,
  openBlock,
  announce,
  techName,
  tickets,
  board,
}: {
  /** The board's cards (`boardCards`). */
  cards: readonly BoardCard[];
  /** The board's blocks. */
  blocks: readonly BlockView[];
  /** The day's blocks still to log: what a block merges with. */
  targets: readonly BlockView[];
  timeZone: string;
  /** A block's popup is open: no menu, and no bar. */
  popupOpen: boolean;
  /** Opens a block's popup, on a pane or a form. */
  openBlock: (id: string, options?: OpenOptions) => void;
  announce: (message: string) => void;
  /** Whose day it is: who a close is written as. */
  techName: string | null;
  /** The tech's tickets not closed (the tray's): their links to Autotask. */
  tickets: readonly TrayTicket[] | undefined;
  /** The board's cards' container. */
  board: RefObject<HTMLElement | null>;
}): BoardActions {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const privacy = usePrivacy();
  const phone = usePhone();
  const menu = useMenu();
  const pickable = useMemo(() => pickableCards(cards), [cards]);
  const [kept, setSelection] = useState<Selection>(NO_SELECTION);
  const [confirm, setConfirm] = useState<BarConfirm | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Selecting several is for a desktop: at a phone's width, it clears.
  const [wasPhone, setWasPhone] = useState(phone);
  if (phone !== wasPhone) {
    setWasPhone(phone);
    if (phone) {
      setSelection(NO_SELECTION);
      setConfirm(null);
      setError(null);
    }
  }
  // A selected card that leaves the board drops out of the selection.
  const live = useMemo(() => new Set(pickable.map((c) => c.key)), [pickable]);
  const selection = useMemo(() => prune(kept, live), [kept, live]);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Where focus went back to when the notice asked for it (the text to copy by hand).
  const noticeFrom = useRef<HTMLElement | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const lastTouch = useRef(Number.NEGATIVE_INFINITY);
  const menuKeyAt = useRef(Number.NEGATIVE_INFINITY);
  // The card to focus once these have left the board (or at once, with none):
  // after an action took the focused card, or the bar, away.
  const pendingFocus = useRef<{ key: string | null; gone: ReadonlySet<string> } | null>(null);

  const picked = pickedCards(pickable, selection);
  const actions = bulkActions(picked);
  const barShown = picked.length > 0 && !popupOpen && !phone;
  // A confirm whose action no longer applies to them all is gone.
  const shownConfirm = confirm !== null && actions.some((a) => a.id === confirm && a.ok) ? confirm : null;

  const buttonOf = (key: string | null): HTMLButtonElement | null =>
    key === null ? null : (board.current?.querySelector<HTMLButtonElement>(`button[data-card-key="${key}"]`) ?? null);
  const cardOf = (key: string | undefined): PickableCard | undefined => pickable.find((c) => c.key === key);

  // Focus on to the next card, once the cards an action took are gone.
  useLayoutEffect(() => {
    const want = pendingFocus.current;
    if (want === null) {
      return;
    }
    if ([...want.gone].some((k) => live.has(k))) {
      return;
    }
    pendingFocus.current = null;
    const focused = document.activeElement;
    const lost =
      focused === null ||
      focused === document.body ||
      !focused.isConnected ||
      (focused instanceof HTMLElement && want.gone.has(focused.dataset.cardKey ?? ""));
    if (lost) {
      buttonOf(want.key)?.focus();
    }
  });
  // "Copied: …" goes after a while, and "Dismissed …" with its Undo.
  useEffect(() => {
    if (notice?.kind !== "copied" && notice?.kind !== "dismissed") {
      return;
    }
    const timer = window.setTimeout(
      () => {
        setNotice((current) => (current === notice ? null : current));
      },
      notice.kind === "copied" ? COPIED_MS : DISMISSED_MS,
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [notice]);
  // A touch's long press keeps the browser's own menu.
  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      if (event.pointerType === "touch") {
        lastTouch.current = performance.now();
      }
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, []);

  // ---- Selecting ----
  const select = (next: Selection) => {
    setSelection(next);
    setError(null);
    setConfirm(null);
    announce(next.keys.size === 0 ? "Selection cleared." : `${String(next.keys.size)} selected.`);
  };
  /** Clears it; false when nothing was selected. From the bar, focus goes back to the board. */
  const clear = (): boolean => {
    if (selection.keys.size === 0) {
      return false;
    }
    const focused = document.activeElement;
    if (focused !== null && bar.current?.contains(focused) === true) {
      pendingFocus.current = { key: selection.anchor, gone: new Set() };
    }
    menu.close();
    select(NO_SELECTION);
    return true;
  };
  const columnOf = (key: string): string[] => {
    const card = cardOf(key);
    return card === undefined ? [] : columnKeys(pickable, card.column);
  };
  const keyOfButton = (button: HTMLElement): string | undefined => button.dataset.cardKey;

  const onKeysToggle = useEffectEvent((button: HTMLElement): boolean => {
    const key = keyOfButton(button);
    if (phone || key === undefined || cardOf(key) === undefined) {
      return false;
    }
    select(toggle(selection, key));
    return true;
  });
  const onKeysExtend = useEffectEvent((from: HTMLElement, to: HTMLElement): boolean => {
    const a = keyOfButton(from);
    const b = keyOfButton(to);
    if (phone || a === undefined || b === undefined) {
      return false;
    }
    const column = columnOf(a);
    if (!column.includes(b)) {
      return false;
    }
    select(extend(selection, a, b, column));
    return true;
  });
  const onKeysClear = useEffectEvent((): boolean => clear());
  useEffect(
    () =>
      registerSelection({
        toggle: (button) => onKeysToggle(button),
        extend: (from, to) => onKeysExtend(from, to),
        clear: () => onKeysClear(),
      }),
    [],
  );

  /** A click with Ctrl, ⌘ or Shift selects; a plain one makes the card the anchor. */
  const pickClick = (key: string) => (event: MouseEvent<HTMLButtonElement>): boolean => {
    if (phone) {
      return false;
    }
    if (event.ctrlKey || event.metaKey) {
      select(toggle(selection, key));
      return true;
    }
    if (event.shiftKey) {
      select(rangeTo(selection, key, columnOf(key)));
      event.currentTarget.focus();
      return true;
    }
    setSelection(anchorAt(selection, key));
    return false;
  };

  // ---- One card's actions ----
  const fail = (text: string) => {
    noticeFrom.current = null;
    setNotice({ kind: "failed", text });
  };
  /** Focus on to the next card once these are gone, if it was on one of them. */
  const focusAfter = (gone: readonly string[], near: string | null) => {
    pendingFocus.current = { key: near, gone: new Set(gone) };
  };
  const dismissOne = async (block: BlockView, reason: DismissReason) => {
    setNotice(null);
    const key = keyOfBlock(block.id);
    try {
      await api.dismissBlock(block.id, reason, null);
    } catch (failure) {
      fail(`Couldn't dismiss ${block.title}: ${refusal(failure)}.`);
      return;
    }
    focusAfter([key], nearCard(pickable, [key]));
    await queryClient.invalidateQueries({ queryKey: ["day"] });
    announce(`Dismissed ${block.title}.`);
    dismissed([block], reason);
  };
  const mergeOne = async (block: BlockView, other: BlockView) => {
    setNotice(null);
    try {
      const merged = await api.mergeBlocks(block.id, other.id);
      focusAfter([keyOfBlock(merged.removed_block_id)], keyOfBlock(merged.block_id));
    } catch (failure) {
      fail(`Couldn't merge them: ${refusal(failure)}.`);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["day"] });
    // The Log form's preview is built from the block's activities (#224).
    await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    announce("Merged into one block.");
  };
  const copy = (text: string) => {
    let written: Promise<void> | null;
    try {
      written = clipboard()?.writeText(text) ?? null;
    } catch {
      written = null;
    }
    const blocked = () => {
      noticeFrom.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setNotice({ kind: "blocked", text });
    };
    if (written === null) {
      blocked();
      return;
    }
    written.then(() => {
      setNotice({ kind: "copied", text });
      announce(`Copied: ${text}`);
    }, blocked);
  };
  /** Autotask's own link to a logged block's ticket (ADR 0066): the tray's, or its submission's. */
  const ticketUrl = (block: BlockView): string | null => {
    const id = block.ticket?.ticketId;
    const tray = tickets?.find((t) => t.autotask_ticket_id === id)?.ticket_url ?? null;
    return tray ?? queryClient.getQueryData<Submission | null>(["submission", block.id])?.ticket_url ?? null;
  };
  const openTicket = (block: BlockView) => {
    const known = ticketUrl(block);
    if (known !== null) {
      window.open(known, "_blank", "noopener,noreferrer");
      return;
    }
    // Opened now, inside the click, so the browser allows it; then sent on
    // once the link is read.
    const tab = window.open("about:blank", "_blank");
    if (tab !== null) {
      tab.opener = null;
    }
    const name = block.ticket === null ? "this ticket" : `ticket ${ticketName(block.ticket)}`;
    void queryClient
      .query({ queryKey: ["submission", block.id], queryFn: () => api.getSubmission(block.id) })
      .then(
        (submission) => {
          const url = submission?.ticket_url ?? null;
          if (url !== null && tab !== null) {
            tab.location.replace(url);
          } else {
            tab?.close();
            fail(`Autotask's link to ${name} isn't known yet.`);
          }
        },
        (failure: unknown) => {
          tab?.close();
          fail(`Couldn't open ${name}: ${refusal(failure)}.`);
        },
      );
  };
  const cardActions: CardMenuActions = {
    timeZone,
    targets,
    blocks,
    open: (id, options) => {
      openBlock(id, options);
    },
    dismiss: (block, reason) => {
      void dismissOne(block, reason);
    },
    merge: (block, other) => {
      void mergeOne(block, other);
    },
    copy,
    openTicket: privacy ? null : openTicket,
  };

  // ---- The selection's actions ----
  const keysOf = (list: readonly BlockView[]) => list.map((b) => keyOfBlock(b.id));
  const reasonLabel = (reason: DismissReason) => DISMISS_REASONS.find((r) => r.reason === reason)?.label ?? reason;
  /** Just dismissed, here or from the popup: said at the foot of the board, with Undo for a while (Discussion #877). */
  const dismissed = (list: readonly BlockView[], reason: DismissReason) => {
    const [only] = list;
    if (only === undefined) {
      return;
    }
    noticeFrom.current = null;
    setNotice({
      kind: "dismissed",
      text:
        list.length === 1
          ? `Dismissed ${only.title} · ${timesOf(only, timeZone)} (${reasonLabel(reason)})`
          : `Dismissed ${count(list.length, "block", "blocks")} (${reasonLabel(reason)})`,
      blockIds: list.map((b) => b.id),
    });
  };
  /** Undo: each back where it was, ready to log. */
  const undoDismiss = async (blockIds: readonly string[]) => {
    setNotice(null);
    let why: unknown = null;
    let restored = 0;
    for (const id of blockIds) {
      try {
        await api.restoreBlock(id);
        restored += 1;
      } catch (failure) {
        why ??= failure;
      }
    }
    await queryClient.invalidateQueries({ queryKey: ["day"] });
    if (why !== null) {
      fail(`Couldn't restore ${restored === 0 ? "it" : "them all"}: ${refusal(why)}.`);
      return;
    }
    announce(restored === 1 ? "Restored it." : `Restored ${count(restored, "block", "blocks")}.`);
  };
  /** What's left selected after part of an action failed, the bar saying why; or nothing, and focus on. */
  const settle = (left: readonly string[], why: string | null, gone: readonly string[], near: string | null) => {
    setConfirm(null);
    if (left.length > 0) {
      setSelection(only(left));
      setError(why);
    } else {
      setSelection(NO_SELECTION);
      setError(null);
      focusAfter(gone, near);
    }
  };
  const bulkDismiss = async (reason: DismissReason) => {
    const list = blocksToLog(picked);
    if (list.length === 0 || busy !== null) {
      return;
    }
    const label = reasonLabel(reason);
    setConfirm(null);
    setError(null);
    setBusy(`Dismissing ${count(list.length, "block", "blocks")}…`);
    const near = nearCard(pickable, keysOf(list));
    const done: BlockView[] = [];
    const left: BlockView[] = [];
    let why: unknown = null;
    for (const block of list) {
      try {
        await api.dismissBlock(block.id, reason, null);
        done.push(block);
      } catch (failure) {
        left.push(block);
        why ??= failure;
      }
    }
    setBusy(null);
    settle(
      keysOf(left),
      left.length === 0
        ? null
        : done.length === 0
          ? `Couldn't dismiss them: ${refusal(why)}.`
          : `Dismissed ${andList(done.map((b) => timesOf(b, timeZone)))}; ${andList(left.map((b) => timesOf(b, timeZone)))} ${left.length === 1 ? "wasn't" : "weren't"}: ${refusal(why)}.`,
      keysOf(done),
      near,
    );
    await queryClient.invalidateQueries({ queryKey: ["day"] });
    if (done.length > 0) {
      announce(`${count(done.length, "block", "blocks")} dismissed as ${label}.`);
      dismissed(done, reason);
    }
  };
  const bulkMerge = async () => {
    const plan = mergeOrder(blocksToLog(picked));
    if (plan === null || plan.others.length === 0 || busy !== null) {
      return;
    }
    const all = [plan.keep, ...plan.others];
    setConfirm(null);
    setError(null);
    setBusy(`Merging ${count(all.length, "block", "blocks")}…`);
    let kept = plan.keep.id;
    const merged: BlockView[] = [plan.keep];
    let left: BlockView[] = [];
    let why: unknown = null;
    for (const [index, other] of plan.others.entries()) {
      try {
        kept = (await api.mergeBlocks(kept, other.id)).block_id;
        merged.push(other);
      } catch (failure) {
        why = failure;
        left = plan.others.slice(index);
        break;
      }
    }
    setBusy(null);
    await queryClient.invalidateQueries({ queryKey: ["day"] });
    await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    if (left.length === 0) {
      setSelection(NO_SELECTION);
      setConfirm(null);
      setError(null);
      openBlock(kept);
      announce(`${count(merged.length, "block", "blocks")} merged into one.`);
      return;
    }
    // Stopped at a failure: what merged is one block now, still selected with the rest.
    const times = (list: readonly BlockView[]) => andList(list.map((b) => timesOf(b, timeZone)));
    setSelection(only([keyOfBlock(kept), ...keysOf(left)]));
    setError(
      merged.length === 1
        ? `Couldn't merge them: ${refusal(why)}. Nothing was merged.`
        : `Merged ${times(merged)} into one; ${times(left)} didn't merge: ${refusal(why)}.`,
    );
    if (merged.length > 1) {
      announce(`${count(merged.length, "block", "blocks")} merged into one; ${count(left.length, "block", "blocks")} didn't merge.`);
    }
  };
  const bulkVendor = async () => {
    const list = blocksToLog(picked);
    if (list.length === 0 || busy !== null) {
      return;
    }
    setError(null);
    setBusy(`Marking ${count(list.length, "block", "blocks")} as a vendor's…`);
    const near = nearCard(pickable, keysOf(list));
    // What's remembered so far: a later block's calls or email may have
    // stopped counting with it, and left the board.
    const remembered = new Set<string>();
    const shown: string[] = [];
    const done: BlockView[] = [];
    const left: BlockView[] = [];
    let why: unknown = null;
    for (const block of list) {
      const { domains, numbers } = vendorParties(block);
      const newDomains = domains.filter((d) => !remembered.has(`email:${d}`));
      const newNumbers = numbers.filter((n) => !remembered.has(`calls:${n}`));
      try {
        const marked = await api.markVendor(block.id, newDomains, newNumbers);
        for (const d of [...newDomains, ...marked.domains]) {
          remembered.add(`email:${d}`);
        }
        for (const n of [...newNumbers, ...marked.numbers]) {
          remembered.add(`calls:${n}`);
        }
        shown.push(...marked.domains, ...marked.numbers);
        done.push(block);
      } catch (failure) {
        const covered =
          domains.every((d) => remembered.has(`email:${d}`)) && numbers.every((n) => remembered.has(`calls:${n}`));
        if (failure instanceof ApiError && failure.status === 404 && covered) {
          // Gone already: its vendor's calls or email stopped counting with an earlier one's.
          done.push(block);
        } else {
          left.push(block);
          why ??= failure;
        }
      }
    }
    setBusy(null);
    settle(
      keysOf(left),
      left.length === 0
        ? null
        : `${andList(left.map((b) => b.title))} ${left.length === 1 ? "wasn't" : "weren't"} marked: ${refusal(why)}.`,
      keysOf(done),
      near,
    );
    await queryClient.invalidateQueries({ queryKey: ["day"] });
    if (done.length > 0) {
      const parties = vendorPartiesShown(shown);
      announce(
        `${count(done.length, "block", "blocks")} dismissed as a vendor's.` +
          (parties === "" ? "" : ` ${parties} won't be counted again.`),
      );
    }
  };
  const bulkClose = async () => {
    const list = ticketsToClose(picked);
    if (list.length === 0 || busy !== null) {
      return;
    }
    setError(null);
    setBusy(`Closing ${count(list.length, "ticket", "tickets")}…`);
    const [first] = list;
    try {
      const result = await api.closeTickets(
        list.map(({ ticket }) => ticket.ticketId),
        crypto.randomUUID(),
      );
      for (const r of result.results) {
        if (r.accepted && r.action !== null) {
          queryClient.setQueryData(ticketActionQuery(r.action.id), r.action);
        }
      }
      const refused = result.results.filter((r) => !r.accepted);
      const accepted = result.results.length - refused.length;
      const cardOfTicket = (id: number) => list.find(({ ticket }) => ticket.ticketId === id);
      setBusy(null);
      settle(
        refused.flatMap((r) => {
          const found = cardOfTicket(r.autotask_ticket_id);
          return found === undefined ? [] : [found.card.key];
        }),
        refused
          .map((r) => {
            const found = cardOfTicket(r.autotask_ticket_id);
            const name = found === undefined ? String(r.autotask_ticket_id) : ticketName(found.ticket);
            return `${name} wasn't closed: ${r.refusal ?? "it can't be closed now"}.`;
          })
          .join(" ") || null,
        [],
        first?.card.key ?? null,
      );
      void queryClient.invalidateQueries({ queryKey: ["day"] });
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      announce(
        refused.length === 0
          ? `Closing ${count(accepted, "ticket", "tickets")}.`
          : `Closing ${count(accepted, "ticket", "tickets")}; ${count(refused.length, "wasn't", "weren't")} closed.`,
      );
    } catch (failure) {
      setBusy(null);
      setConfirm(null);
      setError(`Couldn't close them: ${refusal(failure)}.`);
    }
  };
  const bulkHandlers: BulkMenuActions = {
    dismiss: (reason) => {
      void bulkDismiss(reason);
    },
    merge: () => {
      void bulkMerge();
    },
    ask: (id) => {
      setError(null);
      setConfirm(id);
    },
    clear: () => {
      clear();
    },
  };

  // ---- Opening a menu ----
  /** Not a right-click the board takes: the browser's own menu stays. */
  const browsersOwn = (event: MouseEvent<HTMLElement>): boolean =>
    phone ||
    popupOpen ||
    (event.nativeEvent as Partial<PointerEvent>).pointerType === "touch" ||
    performance.now() - lastTouch.current < TOUCH_MS;
  const openMenuFor = (card: PickableCard, x: number, y: number, opener: HTMLElement | null) => {
    setNotice(null);
    if (selection.keys.size >= 2 && selection.keys.has(card.key)) {
      const label = `${String(picked.length)} selected`;
      menu.openAt(x, y, bulkMenuItems(actions, bulkHandlers), {
        label,
        header: { title: label, line: selectionSummary(picked) },
        opener,
      });
      return;
    }
    if (selection.keys.size > 0 && !selection.keys.has(card.key)) {
      select(NO_SELECTION);
    }
    const [first] = card.blocks;
    if (first?.kind === "logged" && !privacy && ticketUrl(first) === null) {
      // Its link, read before Open ticket in Autotask is chosen.
      queryClient
        .query({ queryKey: ["submission", first.id], queryFn: () => api.getSubmission(first.id) })
        .catch(() => undefined);
    }
    menu.openAt(x, y, cardMenuItems(card, cardActions), {
      label: cardMenuLabel(card, timeZone),
      header: cardMenuHeader(card, timeZone),
      opener,
    });
  };
  const atCorner = (button: HTMLElement | null): { x: number; y: number } => {
    const box = button?.getBoundingClientRect();
    return { x: box?.left ?? 0, y: box?.top ?? 0 };
  };
  const onCardMenu = (card: PickableCard) => (event: MouseEvent<HTMLElement>, button: HTMLButtonElement | null) => {
    if (browsersOwn(event)) {
      return;
    }
    event.preventDefault();
    // The ☰ key's own contextmenu, after its keydown opened the menu.
    if (menu.isOpen && performance.now() - menuKeyAt.current < MENU_KEY_MS) {
      return;
    }
    // From the keyboard, a browser puts it at 0, 0: the card's corner instead.
    const at = event.clientX === 0 && event.clientY === 0 ? atCorner(button) : { x: event.clientX, y: event.clientY };
    openMenuFor(card, at.x, at.y, button);
  };
  const onMenuKey = (card: PickableCard) => (button: HTMLButtonElement) => {
    if (phone || popupOpen) {
      return;
    }
    menuKeyAt.current = performance.now();
    const at = atCorner(button);
    openMenuFor(card, at.x, at.y, button);
  };

  const boardFor = (boardCard: BoardCard): CardBoard | undefined => {
    const card = pickable.find((c) => c.card === boardCard);
    if (card === undefined) {
      return undefined;
    }
    return {
      key: card.key,
      picked: selection.keys.has(card.key) && !phone,
      onPickClick: pickClick(card.key),
      onContextMenu: onCardMenu(card),
      onMenuKey: onMenuKey(card),
    };
  };
  const onCapsuleMenu = (blockId: string, event: MouseEvent<HTMLElement>) => {
    const card = pickable.find((c) => c.blocks.some((b) => b.id === blockId));
    if (card === undefined || browsersOwn(event)) {
      return;
    }
    event.preventDefault();
    openMenuFor(card, event.clientX, event.clientY, buttonOf(card.key));
  };
  const onBoardClick = (event: MouseEvent<HTMLElement>) => {
    if (selection.keys.size === 0 || event.shiftKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    if (target === null || target.closest("li, button, a, input, label, select, textarea") !== null) {
      return;
    }
    clear();
  };

  const closeNotice = () => {
    setNotice(null);
    const back = noticeFrom.current;
    noticeFrom.current = null;
    if (back?.isConnected === true) {
      back.focus();
    }
  };
  const noticeShown = notice !== null && !popupOpen;
  const overlays = (
    <>
      <Menu control={menu} />
      {barShown || noticeShown ? (
        <div
          ref={bar}
          data-part="board-foot"
          className="pointer-events-none fixed inset-x-4 bottom-6 z-40 flex flex-col items-center gap-2"
        >
          {noticeShown ? (
            <BoardNotice
              notice={notice}
              onClose={closeNotice}
              onUndo={() => {
                if (notice.kind === "dismissed") {
                  void undoDismiss(notice.blockIds);
                }
              }}
            />
          ) : null}
          {barShown ? (
            <SelectionBar
              picked={picked}
              actions={actions}
              confirm={shownConfirm}
              busy={busy}
              error={error}
              techName={techName}
              onDismiss={(reason) => {
                void bulkDismiss(reason);
              }}
              onMerge={() => {
                void bulkMerge();
              }}
              onAsk={(id) => {
                setError(null);
                setConfirm(id);
              }}
              onConfirm={() => {
                if (shownConfirm === "vendor") {
                  void bulkVendor();
                } else if (shownConfirm === "close") {
                  void bulkClose();
                }
              }}
              onCancel={() => {
                setConfirm(null);
              }}
              onClear={() => {
                clear();
              }}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );

  return { boardFor, onCapsuleMenu, onBoardClick, barShown, overlays, menu, dismissed };
}

/** What was remembered, as it's said: domains, then numbers as the Vendor… pane shows them. */
function vendorPartiesShown(values: readonly string[]): string {
  return andList([...new Set(values)].map((v) => (v.startsWith("+") ? formatPhone(v) : v)));
}
